import { useAtomValue } from 'jotai';

import type { IndividualPaymentPersonCourseViewModel } from '@/shared/api/generated';
import { useGetApiV1IndividualpaymentPeoplePersonidCourses } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import { individualPaymentAppliedFiltersAtom } from '../store/atoms/individualPaymentFiltersAtom';
import { buildContractQueryParams } from '../utils/buildQueryParams';

/**
 * Janela em que os contratos já carregados são considerados frescos.
 *
 * O `queryClient` do app usa `staleTime: 0`, o que faria cada expandir/recolher
 * remontar o observer e disparar uma requisição nova. Recolher uma linha por
 * engano é comum, então vale um override curto aqui: dentro da janela a
 * reabertura é instantânea e não gera tráfego.
 */
const CONTRACTS_STALE_TIME = 30_000;

/**
 * Contratos financeiros de um aluno (linhas-filhas), carregados sob demanda ao
 * expandir a linha-pai.
 *
 * A query só é habilitada enquanto a linha está aberta e tem
 * {@link CONTRACTS_STALE_TIME} de frescura, de modo que recolher e reabrir a
 * mesma linha serve do cache em vez de refazer a requisição.
 *
 * Os filtros de cobrança aplicados na pesquisa são repassados, senão a
 * subtabela exibiria contratos que não casam com o resultado da linha-pai.
 *
 * @param personId - ID da pessoa (linha-pai).
 * @param isOpen - Se a linha está expandida.
 *
 * @example
 * ```tsx
 * const { contracts, isLoading, isError } = useIndividualPaymentContracts(personId, isOpen);
 * ```
 */
export function useIndividualPaymentContracts(personId: number, isOpen: boolean) {
  const applied = useAtomValue(individualPaymentAppliedFiltersAtom);

  const { data, isLoading, isFetching, isError } =
    useGetApiV1IndividualpaymentPeoplePersonidCourses(personId, buildContractQueryParams(applied), {
      query: { enabled: isOpen, staleTime: CONTRACTS_STALE_TIME },
    });

  // O Kubb tipa o corpo como array, mas o backend pode envelopar em
  // `{ data: [...] }` — sem normalizar, `.map` na subtabela quebra em runtime.
  const contracts: readonly IndividualPaymentPersonCourseViewModel[] =
    unwrapPagedResponse<IndividualPaymentPersonCourseViewModel>(data);

  return {
    contracts,
    isLoading: isOpen && isLoading,
    /** Refetch em andamento sobre dados já exibidos — usado para esmaecer a seção. */
    isFetching: isOpen && isFetching && !isLoading,
    isError,
  } as const;
}
